/*
 * Copyright 2023 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * `removeDecimal` returns a number with the decimal part removed.
 */
export const removeDecimal = (number: number) =>
  number < 0 ? Math.ceil(number) : Math.floor(number);

/**
 * `MaxInt64` is the maximum value of a signed 64-bit integer (2^63 - 1).
 */
export const MaxInt64 = 2n ** 63n - 1n;

/**
 * `MinInt64` is the minimum value of a signed 64-bit integer (-2^63).
 */
export const MinInt64 = -(2n ** 63n);

/**
 * `isWithinInt64Range` reports whether the given bigint fits in a signed
 * 64-bit integer. Values outside this range cannot be stored as a Long
 * without silently wrapping, so callers should reject them.
 */
export function isWithinInt64Range(value: bigint): boolean {
  return value >= MinInt64 && value <= MaxInt64;
}

/**
 * `bigintToBytesLE` converts a signed 64-bit bigint to 8 bytes (little-endian).
 */
export function bigintToBytesLE(value: bigint): Uint8Array {
  const buf = new Uint8Array(8);
  // Interpret as unsigned 64-bit to handle negative values correctly.
  let v = BigInt.asUintN(64, value);
  for (let i = 0; i < 8; i++) {
    buf[i] = Number(v & 0xffn);
    v >>= 8n;
  }
  return buf;
}

/**
 * `uint64FromBytesLE` reads the first 8 bytes (little-endian) of the given
 * array as an unsigned 64-bit bigint.
 *
 * These bytes come off the wire, so their length is whatever a remote peer
 * sent. A byte the payload does not carry reads as zero rather than as
 * `undefined`, which `BigInt()` would reject with a raw TypeError: a truncated
 * payload would then abort the whole snapshot or change decode for every
 * client of the document. Zero padding matches how the other fixed-width
 * primitives read a short payload.
 */
function uint64FromBytesLE(bytes: Uint8Array): bigint {
  let v = 0n;
  for (let i = 7; i >= 0; i--) {
    v = (v << 8n) | BigInt(bytes[i] ?? 0);
  }
  return v;
}

/**
 * `bigintFromBytesLE` reads a signed 64-bit bigint from 8 bytes (little-endian).
 */
export function bigintFromBytesLE(bytes: Uint8Array): bigint {
  return BigInt.asIntN(64, uint64FromBytesLE(bytes));
}

/**
 * `bigintFromBytesLEUnsigned` reads an unsigned 64-bit bigint from 8 bytes (little-endian).
 */
export function bigintFromBytesLEUnsigned(bytes: Uint8Array): bigint {
  return uint64FromBytesLE(bytes);
}

/**
 * `bigintToInt32` truncates a bigint to a signed 32-bit integer.
 */
export function bigintToInt32(value: bigint): number {
  return Number(BigInt.asIntN(32, value));
}
